import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '@cdn/i18n';

/** How long a screen that is still loading may take to show its title before focus stays on the main region */
const WAIT = 3000;

/**
 * After a navigation, moves focus to the title of the new screen and names the document after it. A screen
 * still loading gets the main region first, and its title once it appears — within a bound, and only while
 * the person has not moved focus themselves. Keyboard and screen-reader users land where the content starts
 * instead of staying on the link they followed.
 */
export function RouteFocus() {
	const { t } = useI18n();
	const { pathname } = useLocation();

	useEffect(() => {
		// A title shared by several addresses names the document itself (`data-page-title="Jobs · Storefront"`)
		const name = (title: HTMLElement | null) => {
			const text = title?.getAttribute('data-page-title')?.trim() || title?.textContent?.trim();
			document.title = text ? `${text} · ${t('shell.product')}` : t('shell.product');
		};
		const main = () => document.querySelector<HTMLElement>('main');
		let watch: MutationObserver | undefined;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const stop = () => {
			watch?.disconnect();
			clearTimeout(timer);
		};

		const frame = requestAnimationFrame(() => {
			const title = document.querySelector<HTMLElement>('[data-page-title]');
			(title ?? main())?.focus({ preventScroll: false });
			name(title);
			if (title || typeof MutationObserver === 'undefined') return;

			watch = new MutationObserver(() => {
				const found = document.querySelector<HTMLElement>('[data-page-title]');
				if (!found) return;
				stop();
				// The person may have moved on while the screen loaded; focus is taken back only from the main region
				const active = document.activeElement;
				if (!active || active === document.body || active === main()) found.focus({ preventScroll: false });
				name(found);
			});
			// The body, because before the session is known not even the main region is rendered
			watch.observe(document.body, { childList: true, subtree: true });
			timer = setTimeout(stop, WAIT);
		});
		return () => {
			cancelAnimationFrame(frame);
			stop();
		};
	}, [pathname, t]);

	return null;
}
